import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/common/PageHeader";
import { campaignService } from "@/services";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis,
} from "recharts";
import { Megaphone, Send, CalendarClock, PlayCircle, TrendingUp, UserMinus, Loader2 } from "lucide-react";

const COLORS = ["#0d9488", "#06b6d4", "#f59e0b", "#8b5cf6", "#ec4899", "#64748b"];

function Kpi({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-card rounded-xl border border-border p-5 shadow-sm animate-rise" data-testid={`camp-kpi-${label}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
        <Icon className={`h-5 w-5 ${color}`} />
      </div>
      <p className="text-2xl font-bold text-foreground mt-3">{value ?? "—"}</p>
    </div>
  );
}

function Card({ title, children, testid }) {
  return (
    <div className="bg-card rounded-xl border border-border p-5 shadow-sm" data-testid={testid}>
      <h3 className="font-semibold text-foreground mb-4">{title}</h3>
      {children}
    </div>
  );
}

export default function CampaignDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { campaignService.stats().then(setData).catch(() => {}).finally(() => setLoading(false)); }, []);

  if (loading) return <div className="flex justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const byStatus = (data?.byStatus || []).filter((s) => s.value > 0);
  const byChannel = (data?.byChannel || []).filter((s) => s.value > 0);

  return (
    <div data-testid="campaign-dashboard-page">
      <PageHeader title="Campaign Dashboard" subtitle="Outbound campaign performance across WhatsApp, Email and SMS"
        actions={
          <button onClick={() => navigate("/admin/campaigns/list")} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90" data-testid="camp-go-list">
            <Megaphone className="h-4 w-4" /> View Campaigns
          </button>
        }
      />
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-7">
        <Kpi label="Total" value={data?.total} icon={Megaphone} color="text-violet-500" />
        <Kpi label="Running" value={data?.running} icon={PlayCircle} color="text-teal-500" />
        <Kpi label="Scheduled" value={data?.scheduled} icon={CalendarClock} color="text-sky-500" />
        <Kpi label="Sent" value={data?.sent} icon={Send} color="text-blue-500" />
        <Kpi label="Delivery %" value={`${data?.deliveryRate ?? 0}%`} icon={TrendingUp} color="text-emerald-500" />
        <Kpi label="Opt-outs" value={data?.optedOut} icon={UserMinus} color="text-red-500" />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <Card title="Campaigns by Status" testid="camp-by-status">
          {byStatus.length === 0 ? <p className="text-sm text-muted-foreground py-10 text-center">No campaigns yet</p> : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={byStatus} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                  {byStatus.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
        <Card title="Campaigns by Channel" testid="camp-by-channel">
          {byChannel.length === 0 ? <p className="text-sm text-muted-foreground py-10 text-center">No data</p> : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={byChannel}>
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="value" radius={[6, 6, 0, 0]} fill="#8b5cf6" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>
    </div>
  );
}
